import React, { useState, useEffect } from "react";
import { Outlet, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@xinosolutions/xs-auth-react";
import AppHeader from "./AppHeader";
import AppSidebar from "./AppSidebar";
import { styled } from "@mui/material/styles";
import { ClassRoomProvider } from "../../Hooks/ClassRoom";
import { SIDEBAR_WIDTH } from "../../utils/layoutUtils";
import AppLoader from "../../components/GeneralComponents/AppLoader";
import DashboardSessionError from "../../components/auth/DashboardSessionError";
import { useProductMember } from "../../context/ProductContext";

const APP_BAR_MOBILE = 64;
const APP_BAR_DESKTOP = 65;

const RootStyle = styled("div")({
  display: "flex",
  minHeight: "100%",
  overflow: "visible",
});

const MainStyle = styled("div")(({ theme, pathname }) => ({
  flexGrow: 1,
  overflow: pathname.includes("dashboard") ? "visible" : "auto",
  minHeight: "100%",
  paddingTop: APP_BAR_MOBILE,
  paddingBottom: theme.spacing(10),
  [theme.breakpoints.up("lg")]: {
    paddingTop: APP_BAR_DESKTOP,
    paddingLeft: theme.spacing(1),
    paddingRight: theme.spacing(1),
  },
}));

/**
 * Dashboard shell — auth gate matches xs-template-admin-app (XS Auth).
 * Access-denied UX matches xs-learnix-portal: keep account menu, hide sidebar.
 */
export default function DashboardLayout() {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [drawerWidth, setDrawerWidth] = useState(SIDEBAR_WIDTH);
  const [sessionRetrying, setSessionRetrying] = useState(false);

  const { isAuthenticated, loading: authLoading } = useAuth();
  const { loading: productLoading, ready, member, failureReason, refreshMember } =
    useProductMember();

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handlePermanentDrawer = () => {
    if (drawerWidth) {
      setDrawerWidth(0);
    } else {
      setDrawerWidth(SIDEBAR_WIDTH);
    }
  };

  useEffect(() => {
    if (authLoading) return;
    if (!isAuthenticated) {
      navigate("/", { replace: true });
    }
  }, [authLoading, isAuthenticated, navigate]);

  useEffect(() => {
    setMobileOpen(false);
  }, [pathname]);

  if (authLoading || (isAuthenticated && productLoading)) {
    return <AppLoader wrapperClassName="circular-progress" />;
  }

  if (!isAuthenticated) {
    return null;
  }

  const accessDenied = Boolean(failureReason) || (ready && !member);

  // Signed in but no admin workspace — show account menu, hide sidebar.
  if (accessDenied) {
    return (
      <RootStyle>
        <AppHeader
          drawerWidth={0}
          handleDrawerToggle={handleDrawerToggle}
          handlePermanentDrawer={handlePermanentDrawer}
          hideNavigation
        />
        <MainStyle pathname={pathname}>
          <DashboardSessionError
            reason={failureReason || "Admin access required"}
            retrying={sessionRetrying}
            onRetry={async () => {
              setSessionRetrying(true);
              try {
                await refreshMember();
              } finally {
                setSessionRetrying(false);
              }
            }}
          />
        </MainStyle>
      </RootStyle>
    );
  }

  if (!ready || !member) {
    return <AppLoader wrapperClassName="circular-progress" />;
  }

  return (
    <ClassRoomProvider>
      <RootStyle>
        <AppHeader
          drawerWidth={drawerWidth}
          handleDrawerToggle={handleDrawerToggle}
          handlePermanentDrawer={handlePermanentDrawer}
        />
        <AppSidebar
          drawerWidth={drawerWidth}
          handleDrawerToggle={handleDrawerToggle}
          mobileOpen={mobileOpen}
        />
        <MainStyle pathname={pathname}>
          <Outlet />
        </MainStyle>
      </RootStyle>
    </ClassRoomProvider>
  );
}
